import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CalendarDays, CheckCircle2, Clock, ExternalLink, LoaderCircle, Search, Users } from 'lucide-react'
import { format } from 'date-fns'
import { workloadApi } from '../api/workload'
import { Avatar } from '../components/common/Avatar'
import { formatWorkType } from '../lib/workTypes'
import type { DashboardLog, DashboardMember } from '../types'

const todayDateInput = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

const addDaysToInput = (date: string, amount: number) => {
  const [year, month, day] = date.split('-').map(Number)
  const next = new Date(year, month - 1, day)
  next.setDate(next.getDate() + amount)
  const nextMonth = String(next.getMonth() + 1).padStart(2, '0')
  const nextDay = String(next.getDate()).padStart(2, '0')
  return `${next.getFullYear()}-${nextMonth}-${nextDay}`
}

const parseDateKey = (date: string) => {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const formatHours = (hours: number) => {
  if (!Number.isFinite(hours) || hours <= 0) return '0h'
  const wholeHours = Math.floor(hours)
  const minutes = Math.round((hours - wholeHours) * 60)
  if (minutes === 60) return `${wholeHours + 1}h`
  return minutes > 0 ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`
}

export default function EmployeeLogsDashboardPage() {
  const navigate = useNavigate()
  const today = todayDateInput()
  const [startDate, setStartDate] = useState(today)
  const [endDate, setEndDate] = useState(today)
  const [employeeId, setEmployeeId] = useState('all')
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['employee-logs-dashboard', startDate, endDate, employeeId],
    queryFn: () => workloadApi.getDashboard({ startDate, endDate, employeeId }),
  })

  const dashboard = data?.data?.data
  const days = dashboard?.daily || []
  const normalizedSearch = search.trim().toLowerCase()

  const employeeRows = useMemo(() => {
    return days.flatMap((day) =>
      day.members.map((member) => ({
        day: day.date,
        member,
        logs: member.logs.filter((log) => {
          if (!normalizedSearch) return true
          const haystack = [
            member.user.fullName,
            log.issue.identifier,
            log.issue.title,
            log.issue.project?.name,
            log.notes,
            formatWorkType(log.workType),
            log.status,
          ].join(' ').toLowerCase()
          return haystack.includes(normalizedSearch)
        }),
      }))
    )
  }, [days, normalizedSearch])

  const visibleRows = employeeRows.filter((row) => !normalizedSearch || row.logs.length > 0 || row.member.user.fullName.toLowerCase().includes(normalizedSearch))
  const activeEmployees = new Set(days.flatMap((day) => day.members.filter((member) => member.actualHours > 0).map((member) => member.user.id))).size

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <div>
          <div style={eyebrowStyle}>Employee logs</div>
          <h1 style={titleStyle}>Dashboard 2</h1>
          <p style={subtitleStyle}>Daily team work log with completed work, progressing work, hours, notes, and linked tasks.</p>
        </div>

        <div style={filterBarStyle}>
          {dashboard?.canSelectEmployee && (
            <label style={fieldLabelStyle}>
              Employee
              <select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)} style={selectStyle}>
                <option value="all">All employees</option>
                {dashboard.employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>{employee.fullName}</option>
                ))}
              </select>
            </label>
          )}
          <label style={fieldLabelStyle}>
            Start
            <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            End
            <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} style={inputStyle} />
          </label>
          <button type="button" onClick={() => { setStartDate(today); setEndDate(today) }} style={quickButtonStyle}>Today</button>
          <button type="button" onClick={() => { setStartDate(addDaysToInput(today, -6)); setEndDate(today) }} style={quickButtonStyle}>7 days</button>
        </div>
      </div>

      <div style={statsGridStyle}>
        <Metric icon={<Clock size={18} />} label="Logged" value={formatHours(dashboard?.totalHours || 0)} tone="#2563eb" />
        <Metric icon={<Users size={18} />} label="Active Employees" value={String(activeEmployees)} tone="#0891b2" />
        <Metric icon={<CalendarDays size={18} />} label="Tasks" value={String(dashboard?.taskCount || 0)} tone="#ea580c" />
        <Metric icon={<CheckCircle2 size={18} />} label="Completed" value={String(dashboard?.completedTaskCount || 0)} tone="#16a34a" />
        <Metric icon={<LoaderCircle size={18} />} label="Progressing" value={String(dashboard?.progressingTaskCount || 0)} tone="#7c3aed" />
      </div>

      <div style={searchWrapStyle}>
        <Search size={16} style={{ color: '#64748b' }} />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search employee, task, project, work type, or notes"
          style={searchInputStyle}
        />
      </div>

      {isLoading ? (
        <div style={emptyStyle}>Loading employee logs...</div>
      ) : visibleRows.length === 0 ? (
        <div style={emptyStyle}>No employee logs found.</div>
      ) : (
        <div style={dayStackStyle}>
          {days.map((day) => {
            const rowsForDay = visibleRows.filter((row) => row.day === day.date)
            if (rowsForDay.length === 0) return null

            return (
              <section key={day.date} style={dayPanelStyle}>
                <div style={dayHeaderStyle}>
                  <div>
                    <h2 style={dayTitleStyle}>{format(parseDateKey(day.date), 'EEEE, MMM d, yyyy')}</h2>
                    <div style={dayMetaStyle}>
                      {formatHours(day.totalHours)} logged - {day.taskCount} tasks - {day.completedTaskCount} completed - {day.progressingTaskCount} progressing
                    </div>
                  </div>
                </div>

                <div style={employeeStackStyle}>
                  {rowsForDay.map(({ member, logs }) => (
                    <EmployeeLogCard
                      key={`${day.date}:${member.user.id}`}
                      member={member}
                      logs={logs}
                      onOpenIssue={(issueId) => navigate(`/issues/${issueId}`)}
                    />
                  ))}
                </div>
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}

function Metric({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: string; tone: string }) {
  return (
    <div style={metricStyle}>
      <div style={{ ...metricIconStyle, color: tone, background: `${tone}14` }}>{icon}</div>
      <div>
        <div style={metricLabelStyle}>{label}</div>
        <div style={{ ...metricValueStyle, color: tone }}>{value}</div>
      </div>
    </div>
  )
}

function EmployeeLogCard({ member, logs, onOpenIssue }: { member: DashboardMember; logs: DashboardLog[]; onOpenIssue: (issueId: string) => void }) {
  return (
    <div style={employeeCardStyle}>
      <div style={employeeHeaderStyle}>
        <div style={personStyle}>
          <Avatar user={member.user} size="sm" />
          <div style={{ minWidth: 0 }}>
            <div style={employeeNameStyle}>{member.user.fullName}</div>
          </div>
        </div>
        <div style={employeeSummaryStyle}>
          <SummaryChip label="Logged" value={formatHours(member.actualHours)} tone="#2563eb" />
          <SummaryChip label="Tasks" value={String(member.taskCount)} tone="#475569" />
          <SummaryChip label="Done" value={String(member.completedTaskCount)} tone="#16a34a" />
          <SummaryChip label="Active" value={String(member.progressingTaskCount)} tone="#7c3aed" />
        </div>
      </div>

      {logs.length === 0 ? (
        <div style={noLogsStyle}>No work logged for this day.</div>
      ) : (
        <div style={logListStyle}>
          {logs.map((log) => (
            <button key={log.id} type="button" onClick={() => onOpenIssue(log.issue.id)} style={logRowStyle}>
              <div style={taskMainStyle}>
                <span style={projectDotStyle(log.issue.project?.color || '#64748b')} />
                <span style={issueKeyStyle}>{log.issue.identifier}</span>
                <span style={taskTitleStyle}>{log.issue.title}</span>
              </div>
              <span style={statusPillStyle(log.status)}>{log.status === 'COMPLETED' ? 'Completed' : 'Progressing'}</span>
              <span style={workTypeStyle}>{formatWorkType(log.workType)}</span>
              <span style={hoursStyle}>{formatHours(log.hours)}</span>
              <ExternalLink size={14} style={{ color: '#94a3b8' }} />
              {log.notes && <div style={notesStyle}>{log.notes}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function SummaryChip({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <span style={{ ...summaryChipStyle, color: tone, background: `${tone}12` }}>
      <strong style={summaryChipValueStyle}>{value}</strong>
      <span>{label}</span>
    </span>
  )
}

const pageStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '100%',
  boxSizing: 'border-box',
  padding: 24,
  background: '#f7f8fb',
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 20,
  marginBottom: 18,
}

const eyebrowStyle: React.CSSProperties = {
  color: '#2563eb',
  fontSize: 12,
  fontWeight: 800,
  textTransform: 'uppercase',
}

const titleStyle: React.CSSProperties = {
  margin: '4px 0 6px',
  fontSize: 28,
  lineHeight: 1.15,
  color: '#111827',
}

const subtitleStyle: React.CSSProperties = {
  margin: 0,
  color: '#64748b',
  fontSize: 14,
  maxWidth: 720,
  lineHeight: 1.5,
}

const filterBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 10,
  padding: 12,
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#ffffff',
}

const fieldLabelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  fontSize: 11,
  fontWeight: 800,
  color: '#64748b',
  textTransform: 'uppercase',
}

const inputStyle: React.CSSProperties = {
  height: 34,
  border: '1px solid #dbe3ee',
  borderRadius: 6,
  padding: '0 10px',
  fontSize: 13,
  color: '#111827',
  background: '#ffffff',
}

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  minWidth: 190,
}

const quickButtonStyle: React.CSSProperties = {
  height: 34,
  border: '1px solid #bfdbfe',
  borderRadius: 6,
  background: '#eff6ff',
  color: '#1d4ed8',
  fontWeight: 800,
  cursor: 'pointer',
  padding: '0 13px',
}

const statsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: 12,
  marginBottom: 14,
}

const metricStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minHeight: 74,
  padding: '14px 16px',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#ffffff',
}

const metricIconStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const metricLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#64748b',
  textTransform: 'uppercase',
  fontWeight: 800,
}

const metricValueStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 20,
  fontWeight: 900,
}

const searchWrapStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '0 12px',
  height: 40,
  border: '1px solid #dbe3ee',
  borderRadius: 8,
  background: '#ffffff',
  marginBottom: 18,
}

const searchInputStyle: React.CSSProperties = {
  flex: 1,
  border: 'none',
  outline: 'none',
  color: '#111827',
  fontSize: 13,
  background: 'transparent',
}

const dayStackStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
}

const dayPanelStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#ffffff',
  overflow: 'hidden',
}

const dayHeaderStyle: React.CSSProperties = {
  padding: '16px 18px',
  borderBottom: '1px solid #e2e8f0',
  background: '#f8fafc',
}

const dayTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  color: '#111827',
}

const dayMetaStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: '#64748b',
}

const employeeStackStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: 14,
}

const employeeCardStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  background: '#ffffff',
  overflow: 'hidden',
}

const employeeHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '12px 14px',
  borderBottom: '1px solid #eef2f7',
  background: '#fbfdff',
}

const personStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
}

const employeeNameStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 900,
  color: '#111827',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const employeeSummaryStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: 8,
  marginLeft: 'auto',
}

const summaryChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'baseline',
  justifyContent: 'center',
  gap: 5,
  minHeight: 26,
  borderRadius: 7,
  padding: '0 9px',
  fontSize: 12,
  fontWeight: 800,
  whiteSpace: 'nowrap',
}

const summaryChipValueStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 950,
}

const logListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 12,
  background: '#f8fafc',
}

const logRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(240px, 1fr) 108px 150px 72px 22px',
  gap: 10,
  alignItems: 'center',
  width: '100%',
  border: '1px solid #e2e8f0',
  borderRadius: 7,
  background: '#ffffff',
  padding: '10px 12px',
  cursor: 'pointer',
  textAlign: 'left',
}

const taskMainStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
}

const projectDotStyle = (color: string): React.CSSProperties => ({
  width: 8,
  height: 8,
  borderRadius: 999,
  background: color,
  flexShrink: 0,
})

const issueKeyStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 12,
  color: '#2563eb',
  fontWeight: 900,
  flexShrink: 0,
}

const taskTitleStyle: React.CSSProperties = {
  color: '#111827',
  fontSize: 13,
  fontWeight: 750,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const statusPillStyle = (status: DashboardLog['status']): React.CSSProperties => {
  const color = status === 'COMPLETED' ? '#16a34a' : '#2563eb'
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 24,
    borderRadius: 7,
    padding: '0 8px',
    color,
    background: `${color}14`,
    fontSize: 11,
    fontWeight: 900,
    textTransform: 'uppercase',
  }
}

const workTypeStyle: React.CSSProperties = {
  color: '#475569',
  fontSize: 12,
  fontWeight: 800,
}

const hoursStyle: React.CSSProperties = {
  color: '#111827',
  fontSize: 13,
  fontWeight: 900,
  textAlign: 'right',
}

const notesStyle: React.CSSProperties = {
  gridColumn: '1 / -1',
  color: '#64748b',
  fontSize: 12,
  lineHeight: 1.45,
  paddingTop: 2,
}

const noLogsStyle: React.CSSProperties = {
  padding: '12px 14px',
  color: '#94a3b8',
  fontSize: 13,
  background: '#f8fafc',
}

const emptyStyle: React.CSSProperties = {
  padding: 48,
  textAlign: 'center',
  color: '#94a3b8',
  border: '1px dashed #cbd5e1',
  borderRadius: 8,
  background: '#ffffff',
}
